import type {DcdOwnershipPageType} from 'analytics/dcdOwnershipAnalytics';
import type {DcdOwnershipService} from 'client';
import {ROUTE_PATHS} from 'constant';
import {useMatch, useParams} from 'react-router';

const GROUPER_GROUP_BASE_URL = 'https://grouper.pp.dropbox.com/v2/groups';
const SOURCEGRAPH_SERVER_BLOB_BASE_URL =
  'https://dropbox.sourcegraphcloud.com/r/github.com/dropbox-internal/server/-/blob';
const PAGERDUTY_SERVICE_BASE_URL = 'https://dropbox.pagerduty.com/service-directory';
const SLACK_CHANNEL_BASE_URL = 'https://dropbox.slack.com/archives';
const SERVICES_PP_SERVICE_BASE_URL = 'https://services.pp.dropbox.com/services';

export const CODE_PATH_REFERENCE_TYPE = 'code_path';
export const SERVICE_ROUTE_REFERENCE_TYPE = 'service_route';

export const useDcdOwnershipPageType = (
  referenceType?: string
): DcdOwnershipPageType | undefined => {
  const isAppPage = useMatch(ROUTE_PATHS.TEAMS_APP) !== null;
  const {componentId, serviceId, referenceId} = useParams<{
    componentId?: string;
    serviceId?: string;
    referenceId?: string;
  }>();

  if (isAppPage) {
    return 'app';
  }

  if (referenceId !== undefined) {
    if (referenceType === SERVICE_ROUTE_REFERENCE_TYPE) {
      return 'service_route';
    }
    if (referenceType === CODE_PATH_REFERENCE_TYPE) {
      return 'code_path';
    }
    return undefined;
  }

  if (serviceId !== undefined) {
    return 'service';
  }

  return componentId !== undefined ? 'component' : undefined;
};

export const getGrouperGroupUrl = (ownerGroupName: string) =>
  `${GROUPER_GROUP_BASE_URL}/${encodeURIComponent(ownerGroupName)}`;

export const getSourcegraphCodePathUrl = (codePath: string) =>
  `${SOURCEGRAPH_SERVER_BLOB_BASE_URL}/${codePath
    .replace(/^\/+/, '')
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;

export const getPagerDutyServiceUrl = (pagerdutyAlertingServiceId: string) =>
  `${PAGERDUTY_SERVICE_BASE_URL}/${encodeURIComponent(pagerdutyAlertingServiceId)}`;

export const getSlackChannelUrl = (slackChannelId: string) =>
  `${SLACK_CHANNEL_BASE_URL}/${encodeURIComponent(slackChannelId)}`;

export const getServicesPpServiceUrl = (serviceName: string) =>
  `${SERVICES_PP_SERVICE_BASE_URL}/${encodeURIComponent(serviceName)}/about`;

export const parseIdParam = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const findDcdOwnershipService = (
  services: DcdOwnershipService[] | undefined,
  serviceId: number | undefined
) => services?.find(({id}) => id === serviceId);
